import { GridItem, Grid } from "@chakra-ui/react";
import { withoutNullable } from "@syla/shared/types/helpers/withoutNullable";
import { uniqBy, uniqueId } from "lodash";
import React, { useMemo, useEffect, Fragment } from "react";
import {
  useGetWalletOptions,
  formatWalletOption,
} from "../../../../../../hooks/wallet/useGetWalletOptions";
import { useCurrentAccountStore } from "../../../../../../store/currentAccountStore";
import { useTransactionsStore } from "../../../../../../store/transactionsStore";
import { OrderFilterSetting } from "../../../../../../types/order/orderQuery";
import { StandardSelectOption } from "../../../../../selector/parts/SelectOption";
import { SingleSelector } from "../../../../../selector/SingleSelector";
import { setOperation, ChangeWalletOperationEntry } from "../common";
import { ArrowGridItem } from "../components/ArrowGridItem";
import { DeleteButtonGridItem } from "../components/DeleteButtonGridItem";

export function WalletBulkEdit({
  filters,
  operations,
  selectedIds,
  setOperations,
}: {
  operations: ChangeWalletOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<ChangeWalletOperationEntry[]>
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
}) {
  const groups = useTransactionsStore((s) => s.groups);

  const selectedWalletOptions = useMemo(() => {
    if (!groups || selectedIds.length === 0) {
      return undefined;
    }

    return uniqBy(
      withoutNullable(
        selectedIds.flatMap((gid) =>
          groups[gid].data.transactions.flatMap((transaction) => ({
            wallet: transaction.wallet,
            dataSource: transaction.dataSource,
          })),
        ),
      ),
      ({ wallet }) => wallet._id,
    ).map(({ wallet, dataSource }) =>
      formatWalletOption({
        _id: wallet._id,
        customName: wallet.customName,
        dataSource,
      }),
    );
  }, [groups, selectedIds]);

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const walletOptions = useGetWalletOptions(accountId)?.data;

  const filteredAndSelectedWalletOptions = useMemo(
    () =>
      (selectedWalletOptions ?? []).filter((option) =>
        filters.dataSource.includes(option.value),
      ),
    [selectedWalletOptions, filters.dataSource],
  );

  // prepopulate operations based on selected types or filters
  useEffect(() => {
    if (
      selectedWalletOptions !== undefined &&
      selectedWalletOptions.length === 1
    ) {
      setOperations(
        selectedWalletOptions.map((option) => ({
          id: uniqueId(),
          type: "wallet",
          from: option.value,
        })),
      );
    } else if (
      filteredAndSelectedWalletOptions.length > 0 &&
      filteredAndSelectedWalletOptions.length <= 3
    ) {
      setOperations(
        filteredAndSelectedWalletOptions.map((option) => ({
          id: uniqueId(),
          type: "wallet",
          from: option.value,
        })),
      );
    } else if (
      selectedWalletOptions === undefined &&
      filters.dataSource.length > 0 &&
      filters.dataSource.length <= 3
    ) {
      setOperations(
        filters.dataSource.map((option) => ({
          id: uniqueId(),
          type: "wallet",
          from: option,
        })),
      );
    } else {
      setOperations([{ id: uniqueId(), type: "wallet" }]);
    }
  }, [
    filteredAndSelectedWalletOptions,
    selectedWalletOptions,
    filters.dataSource,
    setOperations,
  ]);

  return (
    <Grid
      w="full"
      templateColumns={{ base: "1fr", sm: "1fr auto 1fr auto" }}
      alignItems="center"
      justifyItems="flex-start"
      columnGap={4}
      rowGap={{ base: 2, sm: 4 }}
    >
      <Fragment>
        {operations.map((op) => (
          <Fragment key={op.id}>
            <WalletOperation
              key={op.id}
              operation={op}
              fromWalletOptions={selectedWalletOptions ?? walletOptions ?? []}
              fromOptionsLoading={!selectedWalletOptions && !walletOptions}
              toWalletOptions={walletOptions ?? []}
              toOptionsLoading={!walletOptions}
              setOperation={setOperation(op, setOperations)}
            />
            {operations.length > 1 && (
              <DeleteButtonGridItem
                operation={op}
                operations={operations}
                setOperations={setOperations}
              />
            )}
          </Fragment>
        ))}
      </Fragment>
    </Grid>
  );
}

function WalletOperation({
  operation,
  setOperation,
  fromWalletOptions,
  fromOptionsLoading,
  toWalletOptions,
  toOptionsLoading,
}: {
  operation: ChangeWalletOperationEntry;
  setOperation: React.Dispatch<
    React.SetStateAction<ChangeWalletOperationEntry>
  >;
  fromWalletOptions: StandardSelectOption[];
  fromOptionsLoading: boolean;
  toWalletOptions: StandardSelectOption[];
  toOptionsLoading: boolean;
}): JSX.Element {
  return (
    <>
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
          placeholder="Select Data Source"
          enableSearch
          value={operation.from}
          onChange={(selection) => {
            setOperation((operation) => ({
              ...operation,
              from: selection,
            }));
          }}
          options={fromWalletOptions}
          isLoading={fromOptionsLoading}
          autoSelectSingleItem
        />
      </GridItem>
      <ArrowGridItem />
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
          placeholder="Select Data Source"
          enableSearch
          value={operation.to}
          onChange={(selection) => {
            setOperation((operation) => ({
              ...operation,
              to: selection,
            }));
          }}
          options={toWalletOptions
            // don't show the same value
            .filter(({ value }) => value != operation.from)}
          isLoading={toOptionsLoading}
        />
      </GridItem>
    </>
  );
}
